import {
  ADMIN_PERSIST_STORE_KEY,
  adminConsoleLogin,
  adminConsoleMessages,
  adminConsoleSliceActions,
  AdminConsoleState,
  appRoutes,
  canManageFiduciarie,
  useAppSelector
} from 'azshared';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu } from '../../components/menu/menu';
import { useAppDispatch } from '../../hooks/redux';
import { MenuItemInterface } from '../../utils/menu-items';
import './admin-console-container.scss';
import { Header } from './menu-header/menu-header';
import { useIntl } from 'react-intl';
import { AppButton } from 'src/components/buttons/app-button';

const adminMenuItems: MenuItemInterface[] = [
  {
    icon: 'category',
    label: adminConsoleMessages.menuItemDashboard,
    section: 'dashboard'
  },
  {
    icon: 'customer-service',
    label: adminConsoleMessages.menuItemCustomerService,
    section: 'customer-service'
  },
  {
    icon: 'fiduciarie',
    label: adminConsoleMessages.menuItemFiduciarie,
    section: 'fiduciarie'
  }
];

export const AdminConsoleContainer = () => {
  const { user, loginStatus, loginError }: AdminConsoleState = useAppSelector((state) => state.features.adminConsole.common);
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const [countLogin, setCountLogin] = useState(0);
  const [menuOpened, setMenuOpened] = useState<boolean>(false);
  const menuItems = useMemo(
    () => (canManageFiduciarie(user) ? adminMenuItems : adminMenuItems.filter(({ section }) => section !== 'fiduciarie')),
    [user]
  );

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.token) {
      try {
        sessionStorage.setItem(ADMIN_PERSIST_STORE_KEY, JSON.stringify(user));
      } catch (e) {
        console.error('Failed to save admin user to sessionStorage:', e);
      }
      return;
    }

    const storedAdmin = sessionStorage.getItem(ADMIN_PERSIST_STORE_KEY);
    if (storedAdmin) {
      try {
        const parsedAdmin = JSON.parse(storedAdmin);
        if (parsedAdmin?.token) {
          dispatch(adminConsoleSliceActions.setUser(parsedAdmin));
          return;
        }
      } catch (e) {
        console.error('Failed to parse admin user from sessionStorage:', e);
      }
    }

    if (!user && countLogin === 0) {
      dispatch(adminConsoleLogin());
      setCountLogin((p) => p + 1);
    }
  }, [user, dispatch, countLogin]);

  const selectedSection = useMemo(() => {
    if (location.pathname.includes('customer-service')) {
      return 'customer-service';
    }
    if (location.pathname.includes('fiduciarie')) {
      return 'fiduciarie';
    }
    return 'dashboard';
  }, [location.pathname]);

  const handleSelectedSectionChanged = useCallback(
    (section?: string) => {
      if (section === 'dashboard') {
        navigate(appRoutes.AdminDashboard);
      } else if (section === 'customer-service') {
        navigate(appRoutes.CustomerService);
      } else if (section === 'fiduciarie') {
        navigate(appRoutes.AdminFiduciarie);
      }
      setMenuOpened(false);
    },
    [navigate]
  );

  const toggleMenu = useCallback(() => {
    setMenuOpened((prev) => !prev);
  }, []);

  const userName = useMemo(() => {
    if (user?.name || user?.surname) {
      return `${user.name ?? ''} ${user.surname ?? ''}`.trim();
    }
    return undefined;
  }, [user]);

  return (
    <Fragment>
      {user && (
        <div className='admin-console-container'>
          <Menu
            menuItems={menuItems}
            selectedSection={selectedSection}
            onSelectedSectionChanged={handleSelectedSectionChanged}
            opened={menuOpened}
            onToggle={toggleMenu}>
            <Header userName={userName} />
          </Menu>

          <div className='content'>
            <Outlet />
          </div>
        </div>
      )}
      {!user && loginStatus === 'failed' && (
        <div className='admin-console-login-error'>
          <p>{formatMessage(loginError === 'NOT_AUTHORIZED' ? adminConsoleMessages.loginNotAuthorized : adminConsoleMessages.loginFailed)}</p>
          <AppButton label={formatMessage(adminConsoleMessages.loginRetry)} onClick={() => dispatch(adminConsoleLogin())} />
        </div>
      )}
    </Fragment>
  );
};
